import '../Uprise.css';
import React, { useState, useEffect } from 'react';

const Leaderboard = ({ initialBoard, isStandalone = false }) => {
  const [activeBoard, setActiveBoard] = useState(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const b = params.get('board') || params.get('tab') || params.get('role');
      if (b && ['hackers', 'levelup', 'college'].includes(b.toLowerCase())) {
        return b.toLowerCase();
      }
    }
    return initialBoard || 'hackers';
  });

  const [boardData, setBoardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [copiedToast, setCopiedToast] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const cutoffs = {
    hackers: { cutoff: 1200, cutoffText: "DANGER ZONE CUTOFF" },
    levelup: { cutoff: 50, cutoffText: "VIP PASS CUTOFF" },
    college: { cutoff: 10, cutoffText: "INCUBATION CUTOFF" }
  };

  // Sync if initialBoard changes
  useEffect(() => {
    if (initialBoard && ['hackers', 'levelup', 'college'].includes(initialBoard.toLowerCase())) {
      setActiveBoard(initialBoard.toLowerCase());
    }
  }, [initialBoard]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setShowAll(false);

    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://account.levelupevents.in';

    fetch(`${API_BASE_URL}/api/v1/uprise/leaderboard?role=${activeBoard}&limit=100`)
      .then(res => res.json())
      .then(data => {
        if (isMounted) {
          if (data.status === 'success' && Array.isArray(data.leaderboard)) {
            setBoardData(data.leaderboard);
          } else {
            setBoardData([]);
          }
          setLoading(false);
        }
      })
      .catch(err => {
        if (isMounted) {
          console.error("Leaderboard fetch error:", err);
          setBoardData([]);
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [activeBoard]);

  const handleBoardChange = (board) => {
    if (board !== activeBoard) {
      setActiveBoard(board);
      setShowAll(false);
      if (typeof window !== 'undefined' && window.location.pathname.includes('leaderboard')) {
        const url = new URL(window.location);
        url.searchParams.set('board', board);
        window.history.replaceState({}, '', url);
      }
    }
  };

  const getShareUrl = () => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://levelupevents.in';
    return `${origin}/leaderboard?board=${activeBoard}`;
  };

  const boardTitle = activeBoard === 'hackers' 
    ? 'Uprise Hackers' 
    : activeBoard === 'levelup' 
    ? 'LevelUp Builders' 
    : 'Student Communities';

  const shareText = `Check out the live Gauntlet Leaderboard rankings for ${boardTitle} on LevelUp Uprise!`;

  const handleCopyLink = async () => {
    const shareUrl = getShareUrl();
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = shareUrl;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopiedToast(true);
      setTimeout(() => setCopiedToast(false), 3000);
    } catch (e) {
      console.error("Clipboard copy failed:", e);
    }
  };

  const renderGenericRow = (row, cutoff) => {
    let classes = "";
    let rankColor = "";
    let scoreColor = "";
    let isDanger = row.rank > cutoff;

    if (row.rank === 1) {
        classes = "border-b border-gold/20 bg-gradient-to-r from-[#FFD700]/20 to-transparent hover:bg-[#FFD700]/30 transition-colors";
        rankColor = "text-[#FFD700] text-2xl font-black";
        scoreColor = "text-[#FFD700] font-bold";
    } else if (row.rank === 2) {
        classes = "border-b border-gold/20 bg-gradient-to-r from-[#C0C0C0]/10 to-transparent hover:bg-[#C0C0C0]/20 transition-colors";
        rankColor = "text-[#C0C0C0] text-2xl font-black";
        scoreColor = "text-[#C0C0C0] font-bold";
    } else if (row.rank === 3) {
        classes = "border-b border-gold/20 bg-gradient-to-r from-[#CD7F32]/10 to-transparent hover:bg-[#CD7F32]/20 transition-colors";
        rankColor = "text-[#CD7F32] text-2xl font-black";
        scoreColor = "text-[#CD7F32] font-bold";
    } else if (isDanger) {
        classes = "border-b border-danger/20 text-white/30 hover:bg-danger/5 transition-colors bg-pitchblack/60";
        rankColor = "text-danger opacity-70";
        scoreColor = "text-danger/70";
    } else {
        classes = "border-b border-gold/20 bg-gold/5 hover:bg-gold/10 transition-colors";
        rankColor = "text-gold bright";
        scoreColor = "";
        if (row.rank === cutoff) {
            classes = "border-b border-gold/30 bg-gold/10 hover:bg-gold/20 transition-colors shadow-[inset_0_0_15px_rgba(212,175,55,0.2)]";
            scoreColor = "text-gold bright font-bold";
        }
    }

    return (
        <tr key={row.rank} className={classes}>
            <td className={`p-6 font-heading ${rankColor}`}>#{row.rank}</td>
            <td className="p-6 tracking-wide font-bold">{row.name}</td>
            <td className="p-6 text-lightgrey text-sm tracking-widest uppercase">{row.organization || row.role || 'Participant'}</td>
            <td className={`p-6 text-right ${scoreColor}`}>{row.total_xp ? `${row.total_xp.toLocaleString()} XP` : '0 XP'}</td>
        </tr>
    );
  };

  const displayedData = showAll ? boardData : boardData.slice(0, 10);
  const shareUrl = getShareUrl();

  return (
    <section id="arena" className="py-24 md:py-32 px-6 bg-obsidian relative border-b border-gold/20">
      {/* Toast Notification for Link Copied */}
      {copiedToast && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[99999] px-6 py-3.5 bg-pitchblack border-2 border-gold text-gold font-heading font-black tracking-widest text-xs uppercase gold-glow shadow-[0_0_30px_rgba(212,175,55,0.6)] flex items-center gap-3">
          <span className="text-base">📋</span>
          <span>LEADERBOARD LINK COPIED TO CLIPBOARD!</span>
        </div>
      )}

      {/* Share Modal Dialog */}
      {isShareModalOpen && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-pitchblack/85 backdrop-blur-md">
          <div className="glass-card sharp border-2 border-gold p-6 sm:p-8 max-w-lg w-full bg-obsidian relative shadow-[0_0_50px_rgba(212,175,55,0.35)]">
            <button 
              onClick={() => setIsShareModalOpen(false)}
              className="absolute top-4 right-4 text-lightgrey hover:text-white font-heading text-xl font-bold px-2 cursor-pointer"
            >
              ✕
            </button>

            <div className="text-center mb-6">
              <h3 className="text-xl sm:text-2xl font-black font-heading text-white tracking-wider mb-2">
                SHARE <span className="text-gold bright">{boardTitle.toUpperCase()}</span>
              </h3>
              <p className="text-xs text-lightgrey font-grotesk tracking-widest uppercase">
                Direct Standalone Leaderboard Link
              </p>
            </div>

            {/* Link Preview & Copy Button */}
            <div className="flex items-center gap-2 mb-6 bg-pitchblack border border-gold/50 p-2">
              <input 
                type="text" 
                readOnly 
                value={shareUrl}
                className="bg-transparent text-white font-mono text-xs px-2 w-full focus:outline-none"
              />
              <button
                onClick={handleCopyLink}
                className="px-4 py-2 bg-gold text-pitchblack font-heading font-bold text-xs tracking-wider uppercase hover:bg-white transition-colors flex-shrink-0 cursor-pointer"
              >
                {copiedToast ? 'COPIED!' : 'COPY'}
              </button>
            </div>

            {/* Quick Share to Social Apps */}
            <div className="grid grid-cols-3 gap-3">
              <a 
                href={`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 border border-green-500/40 bg-green-500/10 hover:bg-green-500 text-green-400 hover:text-black font-heading text-xs font-bold text-center tracking-wider uppercase transition-colors"
              >
                WhatsApp
              </a>
              <a 
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 border border-blue-400/40 bg-blue-400/10 hover:bg-blue-400 text-blue-300 hover:text-black font-heading text-xs font-bold text-center tracking-wider uppercase transition-colors"
              >
                Twitter / X
              </a>
              <a 
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 border border-blue-600/40 bg-blue-600/10 hover:bg-blue-600 text-blue-400 hover:text-white font-heading text-xs font-bold text-center tracking-wider uppercase transition-colors"
              >
                LinkedIn
              </a>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl text-white font-black mb-6 tracking-wider leading-tight">
                  10,000 ENTER. <span className="text-gold bright">1,200 SURVIVE.</span><br/>
                  WHERE DO YOU STAND?
              </h2>
              <p className="text-lightgrey font-grotesk tracking-[0.3em] text-sm uppercase">THE GAUNTLET LEADERBOARDS (LIVE)</p>
          </div>

          {/* Category Tabs and Actions: Tabs left-aligned in 1 line, Copy & Share right-aligned */}
          <div className="w-full flex flex-col lg:flex-row lg:items-end justify-between gap-3 mb-10 border-b border-lightgrey/20 pb-0">
              {/* All three tabs in one single line, left-aligned with compact sizing so they never cut off */}
              <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto whitespace-nowrap scrollbar-none flex-nowrap -mb-[1px]">
                  <button 
                      onClick={() => handleBoardChange('hackers')} 
                      className={`bg-transparent px-2.5 sm:px-3.5 md:px-4 py-3 sm:py-3.5 font-heading text-xs md:text-[13px] font-bold border-b-2 transition-colors tracking-wider uppercase whitespace-nowrap focus:outline-none cursor-pointer ${
                          activeBoard === 'hackers' 
                              ? 'border-gold text-gold' 
                              : 'border-transparent text-lightgrey hover:text-white'
                      }`}
                  >
                      UPRISE HACKERS
                  </button>
                  <button 
                      onClick={() => handleBoardChange('levelup')} 
                      className={`bg-transparent px-2.5 sm:px-3.5 md:px-4 py-3 sm:py-3.5 font-heading text-xs md:text-[13px] font-bold border-b-2 transition-colors tracking-wider uppercase whitespace-nowrap focus:outline-none cursor-pointer ${
                          activeBoard === 'levelup' 
                              ? 'border-gold text-gold' 
                              : 'border-transparent text-lightgrey hover:text-white'
                      }`}
                  >
                      LEVELUP BUILDERS
                  </button>
                  <button 
                      onClick={() => handleBoardChange('college')} 
                      className={`bg-transparent px-2.5 sm:px-3.5 md:px-4 py-3 sm:py-3.5 font-heading text-xs md:text-[13px] font-bold border-b-2 transition-colors tracking-wider uppercase whitespace-nowrap focus:outline-none cursor-pointer ${
                          activeBoard === 'college' 
                              ? 'border-gold text-gold' 
                              : 'border-transparent text-lightgrey hover:text-white'
                      }`}
                  >
                      STUDENT COMMUNITIES
                  </button>
              </div>

              {/* Right-aligned: COPY TO CLIPBOARD and SHARE Buttons */}
              <div className="flex items-center gap-2 sm:gap-2.5 flex-shrink-0 pb-2.5 md:pb-2 flex-wrap justify-end">
                  {/* COPY TO CLIPBOARD Button */}
                  <button
                      onClick={handleCopyLink}
                      className="cursor-pointer px-3 sm:px-4 py-2 sm:py-2.5 bg-gold/15 hover:bg-gold text-gold hover:text-pitchblack border border-gold font-heading text-xs font-bold tracking-wider uppercase transition-all duration-300 shadow-[0_0_15px_rgba(212,175,55,0.25)] hover:shadow-[0_0_25px_rgba(212,175,55,0.5)] flex items-center gap-1.5 group whitespace-nowrap focus:outline-none"
                      title="Copy direct leaderboard link to clipboard"
                  >
                      <svg 
                          className="w-3.5 h-3.5 transition-transform group-hover:scale-110 flex-shrink-0" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                      >
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                      </svg>
                      <span>{copiedToast ? 'COPIED! ✓' : 'COPY TO CLIPBOARD'}</span>
                  </button>

                  {/* SHARE Button */}
                  <button
                      onClick={() => setIsShareModalOpen(true)}
                      className="cursor-pointer px-2.5 sm:px-3.5 py-2 sm:py-2.5 bg-transparent hover:bg-gold/15 text-gold border border-gold/50 hover:border-gold font-heading text-xs font-bold tracking-wider uppercase transition-all duration-300 flex items-center gap-1.5 group whitespace-nowrap focus:outline-none"
                      title="Share to WhatsApp, Twitter, LinkedIn"
                  >
                      <svg 
                          className="w-3.5 h-3.5 transition-transform group-hover:scale-110 flex-shrink-0" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                      >
                          <circle cx="18" cy="5" r="3"></circle>
                          <circle cx="6" cy="12" r="3"></circle>
                          <circle cx="18" cy="19" r="3"></circle>
                          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                      </svg>
                      <span>SHARE</span>
                  </button>
              </div>
          </div>

          <div className="glass-card sharp overflow-hidden border border-gold/30 shadow-[0_0_30px_rgba(0,0,0,0.8)] board-content">
              <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse whitespace-nowrap">
                      <thead>
                          <tr className="border-b border-gold/30 bg-pitchblack/80 text-gold font-heading text-xs tracking-widest uppercase">
                              <th className="p-6 w-24">Rank</th>
                              <th className="p-6">Name</th>
                              <th className="p-6">Organisation</th>
                              <th className="p-6 text-right">Score/XP</th>
                          </tr>
                      </thead>
                      <tbody className="font-grotesk font-semibold text-lg text-white">
                          {loading ? (
                            <tr>
                              <td colSpan="4" className="py-24 px-6 text-center whitespace-normal">
                                <div className="flex items-center justify-center gap-3 text-gold font-heading tracking-widest uppercase text-sm">
                                  <span className="inline-block animate-pulse text-lg">✦</span>
                                  <span>SYNCHRONIZING LIVE ARENA SCORES...</span>
                                </div>
                              </td>
                            </tr>
                          ) : boardData.length === 0 ? (
                            <tr>
                              <td colSpan="4" className="py-24 px-6 text-center whitespace-normal">
                                <div className="max-w-xl mx-auto flex flex-col items-center justify-center text-center">
                                  <div className="text-gold font-heading text-xl md:text-2xl font-bold mb-3 tracking-widest uppercase">
                                    AWAITING ARENA ACTIVITY
                                  </div>
                                  <p className="text-xs md:text-sm text-lightgrey/70 uppercase tracking-wider leading-relaxed text-center">
                                    Live rankings and XP scores will be displayed here in real time as participants complete challenges and earn points.
                                  </p>
                                </div>
                              </td>
                            </tr>
                          ) : (
                            displayedData.map(row => {
                              let html = [];
                              if (row.rank === cutoffs[activeBoard].cutoff + 1) {
                                html.push(
                                  <tr key="cutoff">
                                    <td colSpan="4" className="p-0">
                                        <div className="w-full bg-danger text-pitchblack font-heading font-black text-center py-4 uppercase tracking-[0.2em] text-xs md:text-sm danger-glow relative overflow-hidden">
                                            <div className="absolute inset-0 opacity-20">
                                                <div className="w-[200%] h-full" style={{background: 'repeating-linear-gradient(45deg, transparent, transparent 10px, #000 10px, #000 20px)'}}></div>
                                            </div>
                                            <span className="relative z-10">{cutoffs[activeBoard].cutoffText}</span>
                                        </div>
                                    </td>
                                </tr>
                                );
                              }
                              html.push(renderGenericRow(row, cutoffs[activeBoard].cutoff));
                              return html;
                            })
                          )}
                      </tbody>
                  </table>
              </div>

              {!loading && boardData.length > 10 && (
                  <div className="p-6 text-center border-t border-gold/20 bg-pitchblack/60 flex flex-col sm:flex-row items-center justify-between gap-4">
                      <span className="text-lightgrey font-grotesk text-xs tracking-widest uppercase">
                          {showAll 
                              ? `SHOWING ALL ${boardData.length} CONTENDERS` 
                              : `SHOWING TOP 10 OF ${boardData.length} CONTENDERS`
                          }
                      </span>
                      <div className="flex items-center gap-3 flex-wrap justify-center">
                          <button 
                              onClick={handleCopyLink}
                              className="cursor-pointer px-5 py-3 bg-transparent border border-gold/40 text-gold hover:bg-gold hover:text-pitchblack font-heading text-xs font-bold tracking-[0.2em] uppercase transition-all duration-300 flex items-center gap-2 focus:outline-none"
                              title="Copy Leaderboard Link to Clipboard"
                          >
                              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                              </svg>
                              <span>{copiedToast ? 'COPIED TO CLIPBOARD! ✓' : 'COPY TO CLIPBOARD'}</span>
                          </button>
                          <button 
                              onClick={() => setShowAll(prev => !prev)}
                              className="cursor-pointer px-8 py-3 bg-transparent border border-gold text-gold hover:bg-gold hover:text-pitchblack font-heading text-xs font-bold tracking-[0.2em] uppercase transition-all duration-300 shadow-[0_0_15px_rgba(212,175,55,0.15)] hover:shadow-[0_0_25px_rgba(212,175,55,0.4)] flex items-center gap-2 focus:outline-none"
                          >
                              {showAll ? (
                                  <>
                                      <span>SHOW LESS</span>
                                      <span className="text-xs">▲</span>
                                  </>
                              ) : (
                                  <>
                                      <span>SEE MORE</span>
                                      <span className="text-xs">▼</span>
                                  </>
                              )}
                          </button>
                      </div>
                  </div>
              )}
          </div>
      </div>
    </section>
  );
};

export default Leaderboard;
